import { View, Text, Pressable, ScrollView } from "react-native";
import { useNavigation, CommonActions } from "@react-navigation/native";
import { History, ChevronRight } from "lucide-react-native";
import { Dialog, MoonEmptyState } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { EMBER } from "../../../../onboarding/theme";
import {
  usePastCyclesList,
  OUTCOME_COLORS,
  OUTCOME_ICONS,
  OUTCOME_LABEL_KEYS,
} from "../../../hooks/usePastCyclesList";

type Props = {
  visible: boolean;
  onClose: () => void;
};

/**
 * Past cycles list shown as an in-place dialog (consistent with the other
 * profile modals). Each row opens that cycle's full recap.
 */
export default function PastCyclesModal({ visible, onClose }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("PastCyclesModal");
  const navigation = useNavigation();
  const pastCycles = usePastCyclesList();

  const openCycleRecap = (cycleId: string) => {
    onClose();
    navigation.dispatch(
      CommonActions.navigate("Home", {
        screen: "Care",
        params: { screen: "PastCycleDetail", params: { cycleId } },
      }),
    );
  };

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout="centered"
      closeButton="minimal"
    >
      <View
        style={{
          alignSelf: "center",
          width: 56,
          height: 56,
          borderRadius: 28,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: `${EMBER.colors.warm}14`,
        }}
      >
        <History size={26} color={EMBER.colors.warm} strokeWidth={2} />
      </View>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 20,
          fontWeight: "700",
          color: EMBER.colors.text,
          textAlign: "center",
          marginTop: 14,
          marginBottom: 12,
        }}
      >
        {t("care.all_past_cycles.title")}
      </Text>

      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ maxHeight: 420 }}
        contentContainerStyle={{ paddingBottom: 4 }}
      >
        {pastCycles.length === 0 ? (
          <MoonEmptyState title={t("care.all_past_cycles.empty")} />
        ) : (
          <View style={{ gap: 8 }}>
            {pastCycles.map((cycle) => {
              const outcomeColor = OUTCOME_COLORS[cycle.outcome];
              const OutcomeIcon = OUTCOME_ICONS[cycle.outcome];
              const outcomeLabel = t(OUTCOME_LABEL_KEYS[cycle.outcome]);

              return (
                <Pressable
                  key={cycle.id}
                  onPress={() => {
                    capturePress(
                      pressLabel(`${cycle.cycleType} #${cycle.cycleNumber}`),
                      "past_cycle_row",
                    );
                    openCycleRecap(cycle.id);
                  }}
                  style={({ pressed }) => ({
                    flexDirection: "row",
                    alignItems: "center",
                    borderRadius: 16,
                    borderWidth: 1,
                    borderColor: pressed
                      ? EMBER.colors.border
                      : "rgba(0,0,0,0.06)",
                    backgroundColor: pressed
                      ? "rgba(0,0,0,0.03)"
                      : "rgba(255,255,255,0.7)",
                    overflow: "hidden",
                  })}
                >
                  {/* Accent bar */}
                  <View
                    style={{
                      width: 4,
                      alignSelf: "stretch",
                      backgroundColor: outcomeColor,
                      borderTopLeftRadius: 16,
                      borderBottomLeftRadius: 16,
                    }}
                  />

                  <View
                    style={{
                      flex: 1,
                      flexDirection: "row",
                      alignItems: "center",
                      paddingVertical: 14,
                      paddingHorizontal: 14,
                      gap: 12,
                    }}
                  >
                    {/* Icon */}
                    <View
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 18,
                        backgroundColor: `${outcomeColor}15`,
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <OutcomeIcon
                        size={18}
                        color={outcomeColor}
                        strokeWidth={1.5}
                      />
                    </View>

                    {/* Content */}
                    <View style={{ flex: 1 }}>
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 15,
                            fontWeight: "500",
                            color: EMBER.colors.text,
                          }}
                        >
                          {cycle.cycleType} #{cycle.cycleNumber}
                        </Text>
                        <View
                          style={{
                            paddingHorizontal: 8,
                            paddingVertical: 2,
                            borderRadius: 6,
                            backgroundColor: `${outcomeColor}12`,
                          }}
                        >
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 11,
                              fontWeight: "500",
                              color: outcomeColor,
                            }}
                          >
                            {outcomeLabel}
                          </Text>
                        </View>
                      </View>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 12,
                          color: EMBER.colors.textFaint,
                          marginTop: 2,
                        }}
                      >
                        {cycle.startDate}
                        {cycle.endDate ? ` — ${cycle.endDate}` : ""}
                      </Text>
                    </View>

                    <ChevronRight
                      size={16}
                      color={EMBER.colors.textFaint}
                      strokeWidth={1.5}
                    />
                  </View>
                </Pressable>
              );
            })}
          </View>
        )}
      </ScrollView>
    </Dialog>
  );
}
